import type { JSX } from "react";
import { useEffect, useState } from "react";

import { clockNow } from "@noko-city/shared";

/**
 * Live `HH:MM:SS` clock for an IANA timezone, ticking every second.
 *
 * The one-second tick lives here — and only here — so a city row or page
 * re-renders this span instead of its whole surrounding layout. Callers keep
 * their own label and layout, passing only the classes this span needs.
 *
 * `at` is the instant the carrying document was rendered from (the payload's own
 * `generatedAt`). A server-rendered document and the browser hydrating it must
 * print the same reading or React throws the server's markup away, so a caller
 * that knows that instant passes it and both sides seed `clockNow(timezone, at)`
 * with it. A caller that does not — the guide header has no payload timestamp —
 * seeds nothing: the first render is empty on both sides rather than two clocks
 * that disagree, and the mount effect below fills the live clock, which is what
 * the reader sees from the first second on.
 */
export function CityClock({
  timezone,
  className,
  at,
}: {
  timezone: string;
  className?: string;
  at?: number | null;
}): JSX.Element {
  const [now, setNow] = useState<string | null>(() => (at != null ? clockNow(timezone, at) : null));
  useEffect(() => {
    setNow(clockNow(timezone));
    const id = setInterval(() => setNow(clockNow(timezone)), 1000);
    return () => clearInterval(id);
  }, [timezone]);
  return <span className={className}>{now}</span>;
}
